import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    lazy,
    Suspense,
} from 'react';
import { GlobeMethods } from 'react-globe.gl';

import cls from '@soluto-design/styles/cls';

import { Container, useIsBreakpointDown } from '@soluto-design/react';

import BlockTitleLarge from '../../molecules/BlockTitleLarge';

import blue from './blue.png';

interface GeoJSON {
    features: [];
}

export type Country = {
    mw: number;
    code: string;
    country: string;
    mwDisplay: string;
};

export interface BlockWorldSectionProps {
    title: string;
    countriesByMw: [Country[]];
    badges?: string[];
    text: string;
    rating: number;
    ratingLink: any;
    tooltipText?: string;
    endpoint?: string;
}

export type Content = {
    title: string;
    subtitle: string;
};

const Globe = lazy(() => import('react-globe.gl'));

function mapDomainToColor(value: number) {
    const colorRange = ['#ECF9E5', '#CFF0BC', '#AAE58B', '#86D959', '#63C82D'];

    if (value === -1) {
        return '#fff';
    }

    return colorRange?.[value];
}

function BlockWorldSection({
    title,
    text,
    badges,
    countriesByMw,
    ratingLink,
    rating,
    tooltipText = '{country}',
    endpoint,
}: BlockWorldSectionProps) {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const globeRef = useRef<GlobeMethods | undefined>();
    const isMobile = useIsBreakpointDown('md');

    const [size, setSize] = useState<Record<'height' | 'width', number>>({
        width: 0,
        height: 0,
    });
    const [loaded, setLoaded] = useState(false);
    const [countries, setCountries] = useState<GeoJSON>({ features: [] });

    const allCountries = useMemo(() => {
        return countriesByMw?.reduce((acc, item) => {
            return [...acc, ...item];
        }, []);
    }, [countriesByMw]);

    const isSSR = typeof window === 'undefined';

    useEffect(() => {
        async function getGeoJson() {
            try {
                const res = await fetch(
                    endpoint ?? '/assets/ne_110m_admin_0_countries.geojson',
                );

                const data = await res.json();

                if (!data) {
                    return;
                }

                setCountries(data);
            } catch (error) {
                console.error(error?.message);
                setCountries({ features: [] });
            }
        }

        getGeoJson();
    }, [endpoint]);

    useEffect(() => {
        if (!loaded || typeof window === 'undefined' || !wrapperRef?.current)
            return;

        const onResize = () => {
            if (!wrapperRef.current || !globeRef?.current) return;

            const maxWidth: number = 1440;

            let scrollBarWidth = window.innerWidth - document.body.clientWidth;

            const { width, height } =
                wrapperRef.current.getBoundingClientRect();

            if (width <= 320) {
                setSize({
                    width: 250,
                    height: height > 640 ? 640 : 350,
                });

                globeRef.current.controls().maxDistance =
                    globeRef.current.getGlobeRadius() * 5;

                globeRef.current.controls().enableRotate = false;

                globeRef.current.controls().update();

                return;
            }

            if (width < 768) {
                setSize({
                    width: 550,
                    height: height > 640 ? 640 : 550,
                });

                globeRef.current.controls().maxDistance =
                    globeRef.current.getGlobeRadius() * 3;

                globeRef.current.controls().enableRotate = false;

                globeRef.current.controls().update();

                return;
            }

            if (width >= 768 && width < 992) {
                setSize({ width: 1024, height });

                globeRef.current.controls().maxDistance =
                    globeRef.current.getGlobeRadius() * 2.75;

                globeRef.current.controls().enableRotate = false;

                globeRef.current.controls().update();

                return;
            }

            if (
                width + scrollBarWidth >= 992 &&
                width + scrollBarWidth < 1275
            ) {
                setSize({ width, height });

                globeRef.current.controls().maxDistance =
                    globeRef.current.getGlobeRadius() * 4;

                globeRef.current.controls().enableRotate = true;

                globeRef.current.controls().update();

                return;
            }

            if (
                width + scrollBarWidth >= 1275 &&
                width + scrollBarWidth < 1435
            ) {
                setSize({ width, height });

                globeRef.current.controls().maxDistance =
                    globeRef.current.getGlobeRadius() * 2.5;

                globeRef.current.controls().enableRotate = true;

                globeRef.current.controls().update();

                return;
            }

            if (
                width + scrollBarWidth >= 1275 &&
                width + scrollBarWidth < 1435
            ) {
                setSize({ width, height });

                globeRef.current.controls().maxDistance =
                    globeRef.current.getGlobeRadius() * 2.5;

                globeRef.current.controls().enableRotate = true;

                globeRef.current.controls().update();

                return;
            }

            setSize({ width: maxWidth, height });

            globeRef.current.controls().maxDistance =
                globeRef.current.getGlobeRadius() * 2.5;

            globeRef.current.controls().enableRotate = true;

            globeRef.current.controls().update();
        };

        onResize();

        window.addEventListener('resize', onResize);

        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, [loaded]);

    useEffect(() => {
        if (globeRef?.current && wrapperRef?.current && loaded) {
            globeRef.current.controls().enableZoom = false;
            globeRef.current.controls().autoRotate = true;
            globeRef.current.controls().autoRotateSpeed = 1.25;

            globeRef.current.controls().minDistance = 0;

            globeRef.current.controls().update();
        }
    }, [loaded]);

    const getTooltip = useCallback(
        (d) => {
            if (!d.__threeObj.visible || isMobile) return '';

            let countryData = allCountries?.find(
                (item) => item?.code === d?.properties?.ISO_A2,
            );

            if (!countryData) return '';

            let tooltipContent = '';

            if (tooltipText && tooltipText.indexOf('{country}') !== -1) {
                tooltipContent = tooltipText.replace(
                    '{country}',
                    `${d.properties.NAME}`,
                );
            }

            return `
      <div class="bg-background-primary-opacity rounded-large p-24 w-fit max-w-[236px]">
        <div>
          <p class="text-h300 text-text-static">${countryData?.mwDisplay} <span class="text-b300">MW</span></p>
          <p class="text-b400 text-text-static">${tooltipContent}</p>
        </div>
      </div>
    `;
        },
        [isMobile, allCountries, tooltipText],
    );

    const hexPolygonColor = useCallback(
        (d: { properties: { ISO_A2: string } }) => {
            const { ISO_A2 } = d.properties;
            let value = countriesByMw?.findIndex((item) => {
                return item?.some((countries) => countries?.code === ISO_A2);
            });

            return mapDomainToColor(value);
        },
        [countriesByMw],
    );

    return (
        <section
            data-theme="dark"
            className="overflow-hidden md:min-h-[750px] lg:min-h-[800px]"
            ref={wrapperRef}>
            <div className="relative bg-background-primary py-64 md:px-[calc(72px-var(--soluto-spacing-margin))] md:py-96 xl:px-[calc(72px-var(--soluto-spacing-margin))] xl:py-96">
                <Container className="relative">
                    <div className="row">
                        <div className="col-span-4 md:col-span-5 lg:col-span-5 xl:col-span-7 hd:col-span-5">
                            {!!badges && badges?.length > 0 && (
                                <div className="flex gap-4 mb-xl">
                                    {badges.map((badge, index) => (
                                        <img
                                            key={index}
                                            className="h-48 xl:h-56 w-auto max-w-fit"
                                            alt="Badges"
                                            src={badge}
                                        />
                                    ))}
                                </div>
                            )}
                            <BlockTitleLarge
                                title={title}
                                description={text}
                                rating={{
                                    value: rating,
                                    link: ratingLink,
                                }}
                            />
                        </div>
                    </div>
                </Container>

                {!isSSR && (
                    <Suspense fallback={null}>
                        <div
                            className={cls(
                                'ml-mega md:ml-0 md:absolute md:inset-0 md:h-full md:w-full md:left-[calc(40%-var(--soluto-spacing-gutter)/2)] lg:left-[calc(40%-var(--soluto-spacing-gutter)/2)] hd:left-[calc(40%-var(--soluto-spacing-gutter)/2)] z-10',
                            )}>
                            <Globe
                                ref={globeRef}
                                width={size?.width}
                                height={size?.height}
                                showAtmosphere={false}
                                showGraticules={false}
                                globeImageUrl={blue}
                                backgroundColor="rgba(0,0,0,0)"
                                hexPolygonsData={countries.features}
                                hexPolygonColor={hexPolygonColor as any}
                                hexPolygonCurvatureResolution={3}
                                hexPolygonAltitude={(d) => 0.001}
                                animateIn={false}
                                onGlobeReady={() => {
                                    setLoaded(true);
                                }}
                                hexPolygonLabel={getTooltip}
                                onHexPolygonHover={(d) => {
                                    let rotate = !d;

                                    if (!globeRef?.current) return;

                                    globeRef.current.controls().autoRotate =
                                        rotate;

                                    globeRef.current.controls().update();
                                }}
                                lineHoverPrecision={0.1}
                            />
                        </div>
                    </Suspense>
                )}
            </div>
        </section>
    );
}

export default BlockWorldSection;
